import React, { useMemo } from 'react';
import styles from './styles.styl';
import cn from 'classnames/bind';
import { QuestionTypeEnum, SurveyTypeEnum } from 'src/utils/enum';
import { TextField } from '@consta/uikit/TextField';
import { Radio } from '@consta/uikit/Radio';
import { Text } from '@consta/uikit/Text';
import { Checkbox } from '@consta/uikit/Checkbox';
import {
  IAnalyticAnswer,
  IUserAnswerItem,
} from 'src/redux/features/analytic/types';
import { IconCommentClear } from 'src/assets/icons';
import { Badge } from '@consta/uikit/Badge';
import { plural } from 'src/utils';
import { ChoiceGroup } from '@consta/uikit/ChoiceGroup';

export interface IAnalyticsQuestionItemProps {
  number?: number;
  type: QuestionTypeEnum;
  question: string;
  comment?: string;
  is_required?: boolean;
  userAnswers: Array<IUserAnswerItem>;
  answers: Array<IAnalyticAnswer>;
  points?: number;
  rating?: number;
  isTitle?: boolean;
  typeQuestion?: SurveyTypeEnum;
}

const cx = cn.bind(styles);

const AnalyticsQuestionItem: React.FC<IAnalyticsQuestionItemProps> = ({
  number,
  type,
  question,
  userAnswers,
  answers,
  comment,
  is_required,
  points,
  rating,
  isTitle = false,
  typeQuestion,
}) => {
  const newAnswersMultiRating = useMemo(() => {
    if (type === QuestionTypeEnum.MULTIPLE_POINT_RATING) {
      return answers.map((item) => {
        return {
          id: item?.id,
          value: item?.name,
          rating:
            userAnswers.find((answer) => answer.question_answer_id === item.id)
              ?.rating || null,
        };
      });
    }
  }, [answers, type, userAnswers]);
  return (
    <div className={cx('AnalyticsQuestionItem')}>
      <div className={cx('AnalyticsQuestionItem__title')}>
        <Text>
          {is_required ? (
            <span className={cx('AnalyticsQuestionItem__star')}>*</span>
          ) : (
            ''
          )}
          {!isNaN(+number) ? `${number}. ` : ''}
          {question}{' '}
        </Text>
        {!isNaN(+points) && typeQuestion === SurveyTypeEnum.TEST && (
          <Badge
            label={`${points} ${plural(points, ['балл', 'балла', 'баллов'])}`}
            status="system"
          />
        )}
      </div>
      {comment && (
        <div className={cx('AnalyticsQuestionItem__comment')}>
          <img src={IconCommentClear} />
          <Text>{comment}</Text>
        </div>
      )}
      {type === QuestionTypeEnum.SELF_SELECT &&
        (userAnswers?.length > 0 ? (
          userAnswers.map((answer, index) => (
            <TextField
              key={index}
              value={answer?.value}
              disabled
              width="full"
              size="s"
              className={cx('AnalyticsQuestionItem__input')}
            />
          ))
        ) : (
          <TextField
            disabled
            width="full"
            size="s"
            className={cx('AnalyticsQuestionItem__input')}
          />
        ))}
      {type === QuestionTypeEnum.SINGLE_SELECT && (
        <div className={cx('AnalyticsQuestionItem__list')}>
          {answers.map((answer, index) => (
            <div key={index} className={cx('AnalyticsQuestionItem__check')}>
              <Radio
                checked={userAnswers.some(
                  (ans) => answer?.id === ans?.question_answer_id,
                )}
                disabled
              />
              <Text view={answer?.is_answer_correct ? 'success' : 'primary'}>
                {answer?.name}
              </Text>
            </div>
          ))}
        </div>
      )}
      {type === QuestionTypeEnum.MULTIPLE_SELECT && (
        <div className={cx('AnalyticsQuestionItem__list')}>
          {answers.map((answer, index) => (
            <div key={index} className={cx('AnalyticsQuestionItem__check')}>
              <Checkbox
                checked={userAnswers.some(
                  (ans) => answer?.id === ans?.question_answer_id,
                )}
                disabled
              />
              <Text view={answer?.is_answer_correct ? 'success' : 'primary'}>
                {answer?.name}
              </Text>
            </div>
          ))}
        </div>
      )}
      {type === QuestionTypeEnum.SINGLE_AND_SELF && (
        <div className={cx('AnalyticsQuestionItem__list')}>
          {answers.map((answer, index) => (
            <div key={index} className={cx('AnalyticsQuestionItem__check')}>
              <Radio
                checked={userAnswers.some(
                  (ans) => answer?.id === ans?.question_answer_id,
                )}
                disabled
              />
              <Text view={answer?.is_answer_correct ? 'success' : 'primary'}>
                {answer?.name}
              </Text>
            </div>
          ))}
          {userAnswers
            .filter((answer) => !answer?.question_answer_id)
            .map((answer, index) => (
              <div key={index} className={cx('AnalyticsQuestionItem__check')}>
                <Radio checked disabled />
                <TextField
                  disabled
                  value={answer?.value}
                  size="s"
                  width="full"
                />
              </div>
            ))}
        </div>
      )}
      {type === QuestionTypeEnum.MULTIPLE_AND_SELF && (
        <div className={cx('AnalyticsQuestionItem__list')}>
          {answers.map((answer, index) => (
            <div key={index} className={cx('AnalyticsQuestionItem__check')}>
              <Checkbox
                checked={userAnswers.some(
                  (ans) => answer?.id === ans?.question_answer_id,
                )}
                disabled
              />
              <Text view={answer?.is_answer_correct ? 'success' : 'primary'}>
                {answer?.name}
              </Text>
            </div>
          ))}
          {userAnswers
            .filter((answer) => !answer?.question_answer_id)
            .map((answer, index) => (
              <div key={index} className={cx('AnalyticsQuestionItem__check')}>
                <Checkbox checked disabled />
                <TextField
                  disabled
                  value={answer?.value}
                  size="s"
                  width="full"
                />
              </div>
            ))}
        </div>
      )}
      {type === QuestionTypeEnum.POINT_RATING && (
        <div
          className={cx('AnalyticsQuestionItem__list', {
            AnalyticsQuestionItem__list_title: isTitle,
          })}
        >
          {userAnswers?.length === 0 && (
            <div className={cx('AnalyticsQuestionItem__check')}>
              {isTitle ? (
                <Text size="s" view="secondary">
                  {'без ответа'}
                </Text>
              ) : (
                <ChoiceGroup
                  value={null}
                  items={Array.from(Array(rating)).map((_, i) => i + 1)}
                  getLabel={(item) => item}
                  multiple={false}
                  onChange={() => null}
                  name="ChoiceGroup"
                  className={cx('AnalyticsQuestionItem__choiseGroup')}
                />
              )}
            </div>
          )}
          {userAnswers?.length > 0 &&
            userAnswers
              .filter((answer) => !answer?.question_answer_id)
              .map((answer, index) => (
                <div key={index} className={cx('AnalyticsQuestionItem__check')}>
                  {isTitle ? (
                    <Text size="s" view="secondary">
                      {answer.value}
                    </Text>
                  ) : (
                    <ChoiceGroup
                      value={+answer.value}
                      items={Array.from(Array(rating)).map((_, i) => i + 1)}
                      getLabel={(item) => item}
                      multiple={false}
                      onChange={() => null}
                      name="ChoiceGroup"
                      className={cx('AnalyticsQuestionItem__choiseGroup')}
                    />
                  )}
                </div>
              ))}
        </div>
      )}
      {type === QuestionTypeEnum.MULTIPLE_POINT_RATING && (
        <div
          className={cx('AnalyticsQuestionItem__list', {
            AnalyticsQuestionItem__list_multi: isTitle,
          })}
        >
          {newAnswersMultiRating.map((answer, index) => (
            <div
              key={index}
              className={cx('AnalyticsQuestionItem__checkRating')}
            >
              {isTitle ? (
                <Text size="s" view="secondary">{`${answer.value} - ${
                  answer?.rating || 'без ответа'
                }`}</Text>
              ) : (
                <>
                  <Text
                    size="s"
                    className={cx('AnalyticsQuestionItem__multiGroupText')}
                  >
                    {answer.value}
                  </Text>
                  <ChoiceGroup
                    value={answer?.rating && +answer.rating}
                    items={Array.from(Array(rating)).map((_, i) => i + 1)}
                    getLabel={(item) => item}
                    onChange={() => null}
                    multiple={false}
                    name="ChoiceGroup"
                    className={cx('AnalyticsQuestionItem__choiseGroup')}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalyticsQuestionItem;
